import { IconPlus, IconWebhook } from "@tabler/icons-react";
import { Button, Flex, Typography } from "antd";
import { formatDistanceToNow } from "date-fns";
import { useState } from "react";
import { useWebhooks } from "@/common/artemis-operations/webhooks";
import { toLabel } from "@/common/utils";
import { BaseContainer, CustomIcon, QueryGuard } from "@/components/base";
import { CustomList } from "@/components/custom-list";
import { WebhookActionLabelMap, WebhookStatusBadge } from "./webhook-badges";
import { WebhookCreateWizard } from "./webhook-create-wizard";
import { WebhookDrawer } from "./webhook-drawer";
import { WebhookSecretDialog } from "./webhook-secret-dialog";

/**
 * Workspace settings → Webhooks (ARCH-035). Shown to workspace and organization admins only; the API
 * enforces the same rule.
 */
export function WorkspaceWebhooksSection({
	workspaceId,
}: {
	workspaceId: string;
}) {
	const { data: webhooks, isPending, isError } = useWebhooks(workspaceId);
	const [creating, setCreating] = useState(false);
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [issued, setIssued] = useState<{
		webhookId: string;
		secret: string;
	} | null>(null);

	return (
		<BaseContainer
			title="Webhooks"
			actions={
				<Button
					type="text"
					icon={<CustomIcon icon={IconPlus} />}
					onClick={() => setCreating(true)}
				>
					Webhook
				</Button>
			}
		>
			<QueryGuard isPending={isPending} isError={isError} isNull={false}>
				<CustomList
					data={webhooks ?? []}
					emptyText="webhooks"
					onAdd={() => setCreating(true)}
					onItemClick={(w) => setSelectedId(w.id)}
					renderLeading={() => <CustomIcon icon={IconWebhook} />}
					renderTrailing={(w) => <WebhookStatusBadge status={w.status} />}
					renderItem={(w) => (
						<Flex vertical style={{ minWidth: 0 }}>
							<Typography.Text strong ellipsis>
								{toLabel(w.name)}
							</Typography.Text>
							<Typography.Text>
								{[
									WebhookActionLabelMap[w.action],
									w.lastInvokedAt
										? `last called ${formatDistanceToNow(new Date(w.lastInvokedAt as string), { addSuffix: true })}`
										: "never called",
								].join(" · ")}
							</Typography.Text>
						</Flex>
					)}
				/>
			</QueryGuard>

			{/* Mounted only while open, so each create starts from a fresh form. */}
			{creating && (
				<WebhookCreateWizard
					open
					workspaceId={workspaceId}
					onClose={() => setCreating(false)}
					onCreated={setIssued}
				/>
			)}
			<WebhookDrawer
				webhookId={selectedId}
				workspaceId={workspaceId}
				onClose={() => setSelectedId(null)}
				onRotated={setIssued}
			/>
			<WebhookSecretDialog issued={issued} onClose={() => setIssued(null)} />
		</BaseContainer>
	);
}
